<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';
  import { format as formatDate, formatDistanceToNowStrict } from 'date-fns';

  import { BizCardPreview } from '@client/components/bizcard-preview';
  import { Clipboard } from '@client/components/clipboard';
  import { Icon } from '@client/components/icon';
  import { Porthole } from '@client/components/porthole';
  import { SignaturePreview } from '@client/components/signature-preview';
  import { UserIntegrations } from '@client/components/user-integrations';

  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_ROLES } from '@client/enums';

  import { formatDistance } from '@common/utils/dates';
  import { generateUUID } from '@common/utils/strings';

  export let data = {};
  export let bizcardurl = null;
  export let onClearCache = () => {};
  export let onSendReminder = () => {};
  export let onToggleUserState = () => {};

  /**
   * Whether the panel is in selection mode. Renders a checkbox, hides the action
   * dropdown, and makes panel clicks toggle selection instead of navigating.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * Whether this panel is currently selected.
   * @type {boolean}
   */
  export let selected = false;

  /**
   * Handler for when the panel or its checkbox is clicked in selection mode.
   * @type {(user: Object) => void}
   */
  export let onSelect = () => {};

  let active = false;
  let dropdownRef = null;
  let roles = [];
  let showPreview = false;
  let showUserMenu = false;
  let showBizcardPreview = false;

  const id = `user-roles-${generateUUID()}`;
  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const onClick = (e) => {
    if (selecting) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(data);
      return;
    }

    const element = e.target;

    active = true;

    // dropdownRef is null while selecting — the early return above guarantees it is set here.
    if (dropdownRef === element || dropdownRef?.contains(element)) {
      active = false;
    } else {
      navigate(`/manage/users/${data.email.split('@').shift()}`, {});
    }
  };

  const onMouseUp = () => {
    active = false;
  };

  const onMouseEnter = (type) => {
    if (type === 'bizcard') {
      showBizcardPreview = true;
    } else {
      showPreview = true;
    }
  };

  const onMouseLeave = (type) => {
    if (type === 'bizcard') {
      showBizcardPreview = false;
    } else {
      showPreview = false;
    }
  };

  const onChangeState = () => {
    data.removed = !data.removed;
    onToggleUserState({ ...data, name: user.name });
  };

  let copyingBizcardUrl = false;

  const copyBizcardUrl = (copyFn) => {
    copyingBizcardUrl = true;

    copyFn(data.bizcardurl);

    setTimeout(() => {
      copyingBizcardUrl = false;
    }, 1000);
  };

  $: user = userMetadata({ ...data, features: $userStore.features });
  $: roles = Object.entries(data.roles || {}).sort();
  $: inactive = data?.removed;
  $: color = data.color || 'var(--color-gray-300)';
  $: shortId = user?.email?.split('@').shift();
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="user-panel"
  class:active
  class:inactive
  class:border-primary={selected}
  class:bg-primary-subtle={selected}
  role="button"
  tabindex="0"
  on:click={(e) => (selecting || !inactive) && onClick(e)}
  on:mouseleave={() => (showUserMenu = false)}
  on:mouseup={onMouseUp}
>
  {#if selecting}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="select" on:click|stopPropagation>
      <input
        class="form-check-input"
        type="checkbox"
        checked={selected}
        on:change={() => onSelect(data)}
        aria-label="{$format('label.SELECT')} {shortId}"
      />
    </div>
  {:else}
    <div bind:this={dropdownRef} class="action">
      <Dropdown isOpen={showUserMenu} toggle={() => (showUserMenu = !showUserMenu)}>
        <DropdownToggle tag="div" class="os-icon-more-horizontal" />
        <DropdownMenu>
          {#if !inactive}
            <DropdownItem on:click={() => redirect(`/manage/users/${shortId}`)}>
              {$format('label.EDIT_USER')}
            </DropdownItem>
            <DropdownItem on:click={onChangeState}>{$format('label.DISABLE_USER')}</DropdownItem>
            <DropdownItem divider />
            <DropdownItem on:click={() => onClearCache(user)}>{$format('label.CLEAR_CACHE')}</DropdownItem>
            <DropdownItem divider />
            {#if data.bizcardurl}
              <Clipboard text={data.bizcardurl} let:copy>
                <DropdownItem on:click={copy}>
                  {$format('label.COPY_BUSINESS_CARD_URL')}
                </DropdownItem>
              </Clipboard>
            {/if}
            <DropdownItem on:click={() => onSendReminder(user)}>{$format('label.SEND_REMINDER')}</DropdownItem>
          {:else}
            <DropdownItem on:click={onChangeState}>{$format('label.ENABLE_USER')}</DropdownItem>
          {/if}
        </DropdownMenu>
      </Dropdown>
    </div>
  {/if}
  <div class="person">
    <div class="photo">
      {#key user}
        <Porthole size="xlarge" details={user} {color} />
      {/key}
      <div class="roles">
        {#each roles as [role, active]}
          {#if active}
            <div id="{id}-{role}" class="role {role}" style="background-color: {USER_ROLES[role].color};" />

            <Tooltip target="{id}-{role}" placement="top">
              {$format(USER_ROLES[role].label.long)}
            </Tooltip>
          {/if}
        {/each}
      </div>
    </div>
    <div class="details">
      <div>{user.name}</div>
      {#if user.title}
        <small>{user.title}</small>
      {/if}
      <small class="email">{shortId}</small>
    </div>

    <div class="d-flex justify-content-around gap-1">
      {#if data?.google?.isAliasSync && data?.google?.primaryEmail_original}
        <div class="pill alias">
          <span id="user-alias-{id}">{$format('label.ALIAS')}</span>
          <Tooltip target="user-alias-{id}" placement="top">
            {$format('label.ALIAS_OF', { values: { email: data?.google?.primaryEmail_original.toLowerCase() } })}
          </Tooltip>
        </div>
      {/if}

      {#if data?.office365?.isAliasSync && data?.office365?.mail_original}
        <div class="pill alias">
          <span id="user-alias-{id}">{$format('label.ALIAS')}</span>
          <Tooltip target="user-alias-{id}" placement="top">
            {$format('label.ALIAS_OF', { values: { email: data?.office365?.mail_original.toLowerCase() } })}
          </Tooltip>
        </div>
      {/if}

      {#if data?.office365?.userPrincipalName?.toLowerCase() !== data?.office365?.mail?.toLowerCase()}
        <div class="pill upn">
          <span id="user-upn-{id}">UPN</span>
          <Tooltip target="user-upn-{id}" placement="top">
            {$format('label.UPN_OF', { values: { email: data?.office365?.userPrincipalName.toLowerCase() } })}
          </Tooltip>
        </div>
      {/if}
    </div>

    {#if bizcardurl}
      <div
        class="pill bizcard"
        on:mouseenter={() => onMouseEnter('bizcard')}
        on:mouseleave={() => onMouseLeave('bizcard')}
        role="button"
        tabindex="0"
      >
        <span>{$format('label.BUSINESS_CARD')}</span>
        <Clipboard text={bizcardurl} let:copy>
          <span
            class="copy"
            on:click|preventDefault|stopPropagation={() => copyBizcardUrl(copy)}
            role="button"
            tabindex="0"
          >
            {#if copyingBizcardUrl}
              <Icon name="check" />
            {:else}
              <Icon name="copy" />
            {/if}
          </span>
        </Clipboard>
        <BizCardPreview bind:show={showBizcardPreview} url={bizcardurl} />
      </div>
    {/if}

    {#if !inactive && user.signature}
      <div class="signature">
        <div role="button" tabindex="0" on:mouseenter={onMouseEnter} on:mouseleave={onMouseLeave}>{user.signature}</div>

        <SignaturePreview bind:show={showPreview} bind:user />

        {#if user.signatureLastSeen}
          <div id="last-seen-{id}">
            {formatDistanceToNowStrict(user.signatureLastSeen, {
              addSuffix: false,
              locale: {
                formatDistance
              }
            })}

            <Tooltip target="last-seen-{id}" placement="top">
              {$format('label.SIGNATURE_LAST_SEEN')}
              {formatDistanceToNowStrict(user.signatureLastSeen, { addSuffix: true })}.
            </Tooltip>
          </div>
        {:else}
          <span>-</span>
        {/if}
      </div>
    {/if}

    {#if data?.featurestamped}
      <div class="pill" id="last-seen-stamped-{id}">
        <span>{$format('label.STAMPED')}</span>
        <span>
          {formatDistanceToNowStrict(new Date(data.featurestamped), { addSuffix: false, locale: { formatDistance } })}
        </span>

        <Tooltip target="last-seen-stamped-{id}" placement="top">
          {$format('label.SIGNATURE_LAST_SEEN_STAMPTED')}
          {formatDistanceToNowStrict(new Date(data.featurestamped), { addSuffix: true })}.
        </Tooltip>
      </div>
    {/if}

    {#if data?.featureoutlookaddin}
      <div class="pill" id="last-seen-addin-{id}">
        <span>
          {$format('label.ADDIN')} v{data.featureoutlookaddinmsg}
        </span>
        <span>
          {formatDistanceToNowStrict(new Date(data.featureoutlookaddin), {
            addSuffix: false,
            locale: { formatDistance }
          })}
        </span>

        <Tooltip target="last-seen-addin-{id}" placement="top">
          {$format('label.SIGNATURE_LAST_SEEN_ADDIN')}
          {formatDistanceToNowStrict(new Date(data.featureoutlookaddin), {
            addSuffix: true
          })}.
        </Tooltip>
      </div>
    {/if}

    {#if !inactive}
      <UserIntegrations bind:data />
    {/if}

    {#if inactive}
      <div class="inactive">{$format('label.DISABLED_USER')}</div>
    {/if}
  </div>

  {#if data?.params?.length}
    <div class="fields">
      <div class="heading">Fields</div>
      {#each data?.params as config, index}
        <div class="field">
          {#if config.forceoverride}
            <Icon name="lock" color="danger" id="{id}-{index}-lock" class="text-medium" />
            <Tooltip target="{id}-{index}-lock" placement="top">
              {$format('label.FIELD_LOCKED_BY_ADMIN')}
            </Tooltip>
          {:else}
            <Icon name="lock-open" id="{id}-{index}-unlock" class="text-medium" />
            <Tooltip target="{id}-{index}-unlock" placement="top">
              {$format('label.FIELD_CAN_BE_MODIFIED')}
            </Tooltip>
          {/if}

          {#if config.nosync}
            <Icon name="repeat-off" color="danger" id="{id}-{index}-no-sync" class="text-medium" />
            <Tooltip target="{id}-{index}-no-sync" placement="top">
              {$format('label.FIELD_IS_NOT_BEING_SYNCED')}
            </Tooltip>
          {:else}
            <Icon name="repeat-cw" id="{id}-{index}-sync" class="text-medium" />
            <Tooltip target="{id}-{index}-sync" placement="top">
              {$format('label.FIELD_IS_BEING_SYNCED')}
            </Tooltip>
          {/if}
          <div class="name">{config.name}</div>
          <div class="value">{@html config.default || '&nbsp;'}</div>
        </div>
      {/each}
    </div>
  {/if}
  <pre />
</div>

<style lang="scss" src="./user-panel.scss"></style>
